import { Table, TableBody, TableCell, TableRow } from '~/shadcn/ui/table'
import { Spinner } from '~/shadcn/ui/spinner'
import { Button } from '~/shadcn/ui/button/button'
import { DateTimeFormatter } from '~/@mid_ui/datetime-formatter/DateTimeFormatter'
import { PageContainer } from '~/@mid_ui/page-container/PageContainer'
import { FiltersContainer } from '~/@mid_ui/table/FiltersContainer'
import { CheckCircleIcon } from '~/@mid_ui/icons/CheckCircleIcon'
import { DocumentIcon } from '~/@mid_ui/icons/DocumentIcon'
import { Text } from '~/@mid_ui/typography/Text'
import { TabTitleWrapper } from '~/@pages_account-portfolio/financial-overview/TabTitleWrapper'
import { useAppfilePdfDownload } from '~/@biz_account/useAppfilePdfDownload/useAppfilePdfDownload'
import type { Document, DocumentTypeId } from '../types'
import { DocumentDownloads } from '../documents_ui/document-downloads/DocumentDownloads'
import { NoDocuments } from '../documents_ui/no-documents/NoDocuments'
import { useDocumentsQuery } from './useDocumentsQuery'
import { useFilterTypeIds } from './useFilters'
import { HeadingTwo } from '~/@mid_ui/typography/HeadingTwo'
import { useIsDesktop } from '~/@mid_ui/layout/useIsDesktop'
import { getDocumentTypeLabel } from './utils'

function DocumentsTable() {
  const { isDownloadingFileId, downloadFile } = useAppfilePdfDownload()
  const { filterId, setFilterId, filterTypes } = useFilterTypeIds()

  const isDesktop = useIsDesktop()

  // TODO: add project (from query params ??) - nechapem, co toto robi
  // {project}: {project?: { businessId?: number; stageNumber?: number }}
  // const isFirstResponseEmptyRef = useRef<boolean>(true)

  const { data, isLoading, hasNextPage, fetchNextPage, refetch } =
    useDocumentsQuery({
      typeIds: filterTypes.find((filterType) => filterType.id === filterId)
        ?.value as DocumentTypeId[],
      filterId,
    })

  if (isLoading && !data) {
    return (
      <div className="flex justify-center items-center mt-7 md:mt-10">
        <Spinner className="size-6" />
      </div>
    )
  }

  const items = data?.pages.flatMap(({ items }) => items) ?? []

  const isFiltersApplied = _isFiltersApplied(filterId)

  return (
    <>
      <div className="hidden md:flex border-b border-border">
        <TabTitleWrapper>
          <HeadingTwo>Dokumenty</HeadingTwo>
        </TabTitleWrapper>
      </div>

      <PageContainer className="flex flex-col gap-10 p-5 md:p-10">
        <FiltersContainer colorScheme="light" className="border-b-0 p-0">
          <div className="flex flex-row flex-wrap-reverse overflow-auto items-start justify-between gap-2">
            <div className="flex flex-row gap-3">
              {filterTypes.map(({ text, id }) => {
                const isSelected = id === filterId

                return (
                  <Button
                    key={id}
                    size="small"
                    variant="filter"
                    disabled={isLoading}
                    className={`${isSelected ? 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground' : 'bg-primary-foreground text-primary'} px-2 border-2 border-primary rounded-md`}
                    onClick={() => {
                      setFilterId(id)
                      refetch()
                    }}>
                    {text}
                  </Button>
                )
              })}
            </div>
            <div className="flex flex-row items-end justify-between gap-3">
              <DocumentDownloads />
            </div>
          </div>
        </FiltersContainer>
      </PageContainer>

      <PageContainer className="pb-15 md:pb-30">
        <div>
          {items.length === 0 && (
            <NoDocuments isIconDisplayed={!isFiltersApplied} />
          )}
          {items.length > 0 && (
            <Table aria-label="customized table" className="border-t border-b">
              <TableBody>
                {items.map((row) => {
                  const document = row as Document
                  // `token` je v `/document` doc-u optional — BE secure selektor je
                  // `_id + userId(session) + name + token`, takže bez tokenu sa súbor
                  // stiahnuť NEDÁ (staré Meteor app_files pred token-fixom ho nemajú).
                  const downloadToken = document.token

                  function handleDownload() {
                    if (downloadToken == null) return

                    downloadFile({
                      fileId: document._id,
                      filename: document.name,
                      token: downloadToken,
                    })
                  }

                  const isDownloadDisabled =
                    isDownloadingFileId != null || downloadToken == null

                  return (
                    <TableRow key={row._id}>
                      {isDesktop ? (
                        <>
                          <TableCell className="text-base">
                            <DateTimeFormatter value={row.createdAt} />
                          </TableCell>

                          <TableCell>
                            <CheckCircleIcon />
                          </TableCell>

                          <TableCell className="text-base">
                            {document?.sign}
                          </TableCell>

                          <TableCell className="text-base leading-6 w-[60%]">
                            {getDocumentTypeLabel(document.typeId)}
                          </TableCell>

                          <TableCell className="text-sm leading-6">
                            <Button
                              onClick={handleDownload}
                              variant="ghost"
                              size="xs"
                              disabled={isDownloadDisabled}
                              className="flex flex-row items-center">
                              <div className="flex flex-row items-center gap-1 text-accent">
                                {isDownloadingFileId === document._id ? (
                                  <Spinner />
                                ) : (
                                  <DocumentIcon className="w-6! h-6!" />
                                )}
                                <Text className="font-normal">
                                  {document.contentType === 'application/pdf'
                                    ? 'PDF'
                                    : ''}
                                </Text>
                              </div>
                            </Button>
                          </TableCell>
                        </>
                      ) : (
                        <TableCell className="px-0 py-5">
                          <div className="flex flex-row justify-between">
                            <span className="text-xs">
                              <DateTimeFormatter value={row.createdAt} />
                            </span>
                            <span className="text-xs">{document?.sign}</span>
                          </div>
                          <div className="flex flex-row justify-between items-center">
                            <span className="text-base">
                              {getDocumentTypeLabel(document.typeId)}
                            </span>
                            <Button
                              onClick={handleDownload}
                              variant="ghost"
                              size="xs"
                              disabled={isDownloadDisabled}
                              className="flex flex-row items-center gap-1 px-0">
                              <div className="flex flex-row items-center gap-1 text-accent">
                                {isDownloadingFileId === document._id ? (
                                  <Spinner />
                                ) : (
                                  <DocumentIcon className="w-6! h-6!" />
                                )}
                                <Text className="font-normal">
                                  {document.contentType === 'application/pdf'
                                    ? 'PDF'
                                    : ''}
                                </Text>
                              </div>
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
          <div className="flex justify-center items-center pt-7 md:pt-10">
            {hasNextPage && (
              <Button
                variant="outline"
                disabled={isLoading}
                onClick={() => fetchNextPage()}>
                Zobraziť ďalšie dokumenty
              </Button>
            )}
          </div>
        </div>
      </PageContainer>
    </>
  )
}

export { DocumentsTable }

function _isFiltersApplied(documentTypeId: number) {
  return documentTypeId !== 1
}
